import { useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Save, Loader2, Camera, X, ClipboardList } from "lucide-react";
import { chUpdateEmpresa, chSubirFotoEmpresa, chBorrarFotoEmpresa, fotoUrl } from "@/lib/prevelis";
import { Campo, visible } from "@/pages/SuizaFields";

export default function SuizaCuestionarioTecnico({ empresaId, schema, initialData, initialFotos, onSaved }) {
  const [idioma, setIdioma] = useState("es");
  const [pt, setPt] = useState(initialData || {});
  const [fotos, setFotos] = useState(initialFotos || []);
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const L = (o) => (idioma === "fr" ? o.fr : o.es) || o.es;
  const set = (k, v) => setPt((s) => ({ ...s, [k]: v }));

  const guardar = async () => {
    setSaving(true);
    try {
      await chUpdateEmpresa(empresaId, { perfil_tecnico: pt });
      toast.success(idioma === "fr" ? "Questionnaire enregistré" : "Cuestionario técnico guardado");
      onSaved && onSaved(pt);
    } catch { toast.error(idioma === "fr" ? "Échec de l'enregistrement" : "No se pudo guardar"); }
    finally { setSaving(false); }
  };
  const subir = async (bloque, file) => {
    try { const f = await chSubirFotoEmpresa(empresaId, file, bloque); setFotos((x) => [...x, f]); }
    catch { toast.error(idioma === "fr" ? "Échec du téléversement" : "No se pudo subir la foto"); }
  };
  const borrar = async (fid) => {
    try { await chBorrarFotoEmpresa(empresaId, fid); } catch (e) { console.error(e); }
    setFotos((x) => x.filter((f) => f.id !== fid));
  };

  const answered = (sec) => sec.fields.reduce((n, f) => {
    const v = pt[f.k];
    return n + ((Array.isArray(v) ? v.length > 0 : !!(v && String(v).trim())) ? 1 : 0);
  }, 0);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="ch-cuestionario-tecnico">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-blue-700" />
          <div>
            <h3 className="font-heading font-semibold text-lg">Cuestionario técnico común</h3>
            <p className="text-sm text-slate-500">Base para toda la documentación. Solo se pregunta una vez; la IA lo reutiliza en cada documento.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1" data-testid="ch-ct-idioma">
            {["es", "fr"].map((l) => (
              <button key={l} onClick={() => setIdioma(l)} data-testid={`ch-ct-idioma-${l}`}
                className={`h-8 px-3 rounded-lg text-sm font-medium border ${idioma === l ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300"}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <button onClick={guardar} disabled={saving} data-testid="ch-ct-guardar"
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {(schema || []).map((sec) => {
          const isOpen = !!open[sec.id];
          const secFotos = fotos.filter((f) => f.bloque === sec.id);
          return (
            <div key={sec.id} className="border border-slate-200 rounded-lg" data-testid={`ch-ct-sec-${sec.id}`}>
              <button onClick={() => setOpen((s) => ({ ...s, [sec.id]: !s[sec.id] }))} data-testid={`ch-ct-toggle-${sec.id}`}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left">
                {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <span className="font-medium text-slate-800 text-sm flex-1">{L(sec)}</span>
                {answered(sec) > 0 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">{answered(sec)}</span>}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-3 border-t border-slate-100 pt-3">
                  {sec.fields.filter((f) => visible(f, pt)).map((f) => (
                    <div key={f.k}>
                      <label className="text-xs font-medium text-slate-600">{L(f)}</label>
                      <Campo field={f} value={pt[f.k]} onChange={(v) => set(f.k, v)} idioma={idioma} />
                    </div>
                  ))}
                  {sec.foto && (
                    <div>
                      <label className="text-xs font-medium text-slate-600">{idioma === "fr" ? "Photos / documents de ce bloc" : "Fotos / documentos de este bloque"}</label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {secFotos.map((f) => (
                          <div key={f.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                            <img src={fotoUrl(f.url)} alt="" className="w-full h-full object-cover" />
                            <button onClick={() => borrar(f.id)} data-testid={`ch-ct-foto-del-${f.id}`} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <label data-testid={`ch-ct-foto-add-${sec.id}`} className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 cursor-pointer hover:border-blue-400">
                          <Camera className="w-5 h-5" />
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) subir(sec.id, e.target.files[0]); e.target.value = ""; }} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
